import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';
import { PagedResponse, PageQuery, toPageParams } from '../models/paged.types';

/** Lightweight row returned by the paged step execution list (no CustomData/ExecutionData). */
export interface FlowStepExecutionSummary {
  Id: string;
  CreatedAt: number;
  StepId: string | null;
  StepType: string | null;
  StepAddress: string | null;
  ActivityId: string | null;
  ActivityExecutionId: string | null;
}

export interface FlowStepExecutionContext {
  FlowStepHandlerName: string;
  FlowStepHandlerVersion: string;
  StartExecutionTime: number;
  ExecutionCompletedTime: number | null;
  ExecutionDuration: number | null;
}

export interface InstanceDataOperation {
  [key: string]: unknown;
}

export interface ExecutionDataModel {
  OutputMessage: string | null;
  Output: Record<string, unknown> | Array<unknown>;
  IsSuccessful: boolean;
  InstanceDataOperations: Record<string, InstanceDataOperation>;
}

export interface FlowStepExecution {
  Id: string;
  CreatedAt: string;
  LastUpdatedAt: string;
  StepId: string;
  StepType: string;
  StepAddress: string;
  CustomData: Record<string, unknown>;
  ExecutionData: ExecutionDataModel | null;
  ExecutionContext: FlowStepExecutionContext | null;
  ActivityExecutionId: string | null;
  ActivityId: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class FlowStepExecutionService {
  private api: AxiosInstance | null = null;
  private platformId = inject(PLATFORM_ID);
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.api = axios.create({
        headers: {
          'Content-Type': 'application/json'
        }
      });

      this.api.interceptors.request.use((config) => {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;

        const token = this.authService.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    }
  }

  async getAll(): Promise<FlowStepExecution[] | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get('/flowstepexecution');
      return response.data;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async getPage(query: PageQuery): Promise<PagedResponse<FlowStepExecutionSummary>> {
    if (!this.api) {
      return { Items: [], PageInfo: { TotalItems: 0, TotalPages: 0, CurrentPage: query.pageNumber, PageSize: query.pageSize, TotalIsCapped: false } };
    }

    try {
      const response = await this.api.get('/flowstepexecution', { params: toPageParams(query) });
      return response.data;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async get(id: string): Promise<FlowStepExecution | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get(`/flowstepexecution/${id}`);
      return response.data;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async create(execution: Partial<FlowStepExecution>): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.post('/flowstepexecution', execution);
      return response;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async delete(id: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.delete(`/flowstepexecution/${id}`);
      return response;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }
}
